/** Keep the reader's top visible message still while rows above it change
 * height. Chat turns browser overflow anchoring off (it followed the footer
 * as replies streamed) and Safari never implemented it, so RN Web's estimated
 * spacers, late markdown/code layout, and images otherwise move the
 * transcript under the reader. Positions are measured within the content, so
 * scrolling never registers as drift; only layout does.
 * One instance belongs to one conversation.
 */
const MESSAGE_ROW_SELECTOR = '[id^="msg-hl-"]';

export function createChatScrollAnchor({ now = Date.now, selector = MESSAGE_ROW_SELECTOR } = {}) {
  let anchor = null;
  let pausedUntil = 0;

  const contentTopOf = (node, element, viewportTop) => (
    node.getBoundingClientRect().top - viewportTop + element.scrollTop
  );

  // Rows are in document order, so the first one still on screen can be
  // found by bisection instead of measuring every mounted row per event.
  const firstVisibleRow = (element, viewportTop) => {
    const rows = element.querySelectorAll?.(selector);
    if (!rows?.length) return null;
    let lo = 0;
    let hi = rows.length - 1;
    let found = null;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (rows[mid].getBoundingClientRect().bottom > viewportTop + 1) {
        found = rows[mid];
        hi = mid - 1;
      } else {
        lo = mid + 1;
      }
    }
    return found;
  };

  /** Correct layout drift of the previous anchor when `adjust` is set, then
   * record the row now at the top. Returns the applied scroll delta. */
  const sync = (element, { adjust = false } = {}) => {
    if (!element?.isConnected || !(element.clientHeight > 0)) {
      anchor = null;
      return 0;
    }
    const viewportTop = element.getBoundingClientRect().top;
    let applied = 0;
    if (adjust && now() >= pausedUntil && anchor?.element === element && anchor.node.isConnected) {
      const drift = contentTopOf(anchor.node, element, viewportTop) - anchor.contentTop;
      if (Math.abs(drift) >= 1) {
        const before = element.scrollTop;
        element.scrollTop = before + drift;
        applied = element.scrollTop - before;
      }
    }
    const node = firstVisibleRow(element, viewportTop);
    anchor = node ? { element, node, contentTop: contentTopOf(node, element, viewportTop) } : null;
    return applied;
  };

  // Programmatic smooth scrolls (search, scroll-to-top) mount rows as they
  // pass; a correction mid-animation would cancel them.
  const pause = (ms = 1500) => {
    anchor = null;
    pausedUntil = now() + ms;
  };

  return { sync, pause };
}
